import type { Node, Edge } from '@xyflow/react';

// ============================================================
// Constants
// ============================================================

/** The single department rendered GREEN in the scoped org chart. Every other
 *  in-scope node renders RED. Matched against a node's Arabic name (name_ar,
 *  falling back to name). */
export const ANCHOR_DEPARTMENT_NAME = 'قسم الدراسات والاستراتيجية';

// ============================================================
// Types
// ============================================================

export interface TreeNode {
  id: number;
  name: string;
  name_ar: string;
  unit_type: 'department' | 'section' | 'subsidiary';
  parent: number | null;
  order: number;
  employees_count: number;
  strategic_goals?: { id: number; title: string }[];
  children: TreeNode[];
}

export interface TreeResponse {
  tree: TreeNode[];
  primary_color: string;
  secondary_color: string;
}

export interface OrgNodeData {
  treeNode: TreeNode;
  label: string;
  level: number;
  isExpanded: boolean;
  childrenCount: number;
  primaryColor: string;
  secondaryColor: string;
  onToggleExpand: (nodeId: number) => void;
  onNodeClick: (node: TreeNode) => void;
  /** True when this node falls inside the current user's drill-in scope.
   *  In-scope nodes are clickable; out-of-scope nodes render gray and ignore
   *  clicks. When undefined (legacy callers), treat as in-scope. */
  inScope?: boolean;
  /** True only for the "قسم الدراسات والاستراتيجية" department. Rendered GREEN;
   *  all other in-scope nodes render RED. When scope isn't provided (legacy
   *  callers) every in-scope node is treated as an anchor so the chart keeps its
   *  all-green look. */
  isAnchor?: boolean;
}

// ============================================================
// Circular Reference Check
// ============================================================

export function isAncestorOf(
  potentialAncestorId: string,
  nodeId: string,
  edges: Edge[],
): boolean {
  const parentMap = new Map<string, string>();
  for (const edge of edges) {
    parentMap.set(edge.target, edge.source);
  }
  let current: string | undefined = nodeId;
  const visited = new Set<string>();
  while (current !== undefined) {
    if (current === potentialAncestorId) return true;
    if (visited.has(current)) break;
    visited.add(current);
    current = parentMap.get(current);
  }
  return false;
}

// ============================================================
// Tree → React Flow Conversion
// ============================================================

export function treeToNodesEdges(
  tree: TreeNode[],
  options: {
    primaryColor: string;
    secondaryColor: string;
    expandedNodeIds: Set<number>;
    onToggleExpand: (nodeId: number) => void;
    onNodeClick: (node: TreeNode) => void;
    /** Optional set of unit IDs the user can drill into. When provided,
     *  nodes outside this set are rendered as out-of-scope (gray, no click).
     *  Omit to keep every node in-scope (legacy behaviour). */
    inScopeIds?: Set<number>;
  },
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  function walk(treeNode: TreeNode, level: number) {
    const nodeId = String(treeNode.id);
    const isExpanded = options.expandedNodeIds.has(treeNode.id);
    const childrenCount = treeNode.children?.length ?? 0;
    const scoped = options.inScopeIds !== undefined;
    const inScope = !scoped ? true : options.inScopeIds!.has(treeNode.id);
    // The anchor is the user's own department "قسم الدراسات والاستراتيجية": it
    // stays GREEN while every other in-scope node turns RED. Match it by name so
    // the green node is always the strategy/studies section regardless of tree
    // position. With no scope provided (legacy callers) treat every in-scope node
    // as an anchor → keeps the all-green look.
    const nodeName = (treeNode.name_ar || treeNode.name || '').trim();
    const isAnchor = !scoped ? true : (inScope && nodeName === ANCHOR_DEPARTMENT_NAME);

    nodes.push({
      id: nodeId,
      type: 'orgUnit',
      position: { x: 0, y: 0 }, // dagre will set this
      data: {
        treeNode,
        label: treeNode.name_ar || treeNode.name,
        level,
        isExpanded,
        childrenCount,
        primaryColor: options.primaryColor,
        secondaryColor: options.secondaryColor,
        onToggleExpand: options.onToggleExpand,
        onNodeClick: options.onNodeClick,
        inScope,
        isAnchor,
      } satisfies OrgNodeData,
    });

    if (treeNode.parent !== null) {
      edges.push({
        id: `e-${treeNode.parent}-${treeNode.id}`,
        source: String(treeNode.parent),
        target: nodeId,
        type: 'smoothstep',
        style: { stroke: '#9CA3AF', strokeWidth: 1.5 },
      });
    }

    if (isExpanded && treeNode.children) {
      for (const child of treeNode.children) {
        walk(child, level + 1);
      }
    }
  }

  for (const root of tree) {
    walk(root, 0);
  }

  return { nodes, edges };
}

// ============================================================
// Collect all node IDs from tree
// ============================================================

export function collectAllIds(nodes: TreeNode[]): number[] {
  const ids: number[] = [];
  function walk(node: TreeNode) {
    ids.push(node.id);
    node.children?.forEach(walk);
  }
  nodes.forEach(walk);
  return ids;
}
